import { inject, Injectable } from '@angular/core';
import { SideNavigationList } from '@xt/ui-kit';
import { combineLatest, map, Observable, of } from 'rxjs';
import { ContextNavigation } from './side-navigation.models';
import { SideNavigationConfig } from './side-navigation.config';
import { nonNullable } from '@xt/core/utils';
import {
  UserInformationProvider,
  UserInformationProviderContext,
} from '@xt/user-context';
import { select, Store } from '@ngrx/store';
import { userRoleNewSelector } from '@core/store/selectors/user-role.selectors';
import { USER_ROLE } from '@core/store/reducers/session-info.reducer';

@Injectable({
  providedIn: 'root',
})
export class SideNavigation {
  private userContext = inject(UserInformationProvider);
  private navigationConfig = inject(SideNavigationConfig);

  public navList$: Observable<SideNavigationList[]>;

  constructor(private store: Store) {
    this.buildNavigationList();
  }

  private buildNavigationList(): void {
    this.navList$ = combineLatest([
      this.userContext.context$,
      this.store.pipe(select(userRoleNewSelector)),
    ]).pipe(
      map(([context, roles]) => {
        const navigationList = [this.buildMainSideNavigationList()];

        if (roles) {
          navigationList.push(
            this.buildContextSideNavigationList(context, roles),
          );
        }

        return navigationList?.filter(nonNullable) ?? [];
      }),
    );
  }

  private buildMainSideNavigationList(): SideNavigationList {
    return {
      showContext: false,
      items: [...this.navigationConfig.mainNavigationConfig],
    };
  }

  private buildContextSideNavigationList(
    context: UserInformationProviderContext,
    roles: any[],
  ): SideNavigationList {
    if (roles.includes(USER_ROLE.PROVIDER)) {
      return this.getNavContextItemsByConfig(
        this.navigationConfig.providerPatientContextConfig(context),
      );
    }

    if (roles.includes(USER_ROLE.VERIFIED_REPRESENTATIVE)) {
      return this.getNavContextItemsByConfig(
        this.navigationConfig.representativePatientContextConfig(context),
      );
    }
  }

  private getNavContextItemsByConfig(
    config: ContextNavigation,
  ): SideNavigationList {
    return new SideNavigationList({
      showContext: true,
      title: config.style.title,
      icon: config.style.icon,
      actionIcon: config.style.actionIcon,
      actionIconTitle: config.style.actionIconTitle,
      class: config.style.class,
      context: config.context,
      contextPlaceholder: config.style.context,
      contextRouterLink: config.contextRouterLink,
      items: [...this.navigationConfig.contextSelectedNavigationConfig(config)],
    });
  }
}
